/**
 * Brand kit — LampTwist's own company material inside mosy, in two
 * tabs: the trilingual company profile (bio in three lengths, key
 * figures, timeline, contact) and the logo library with the brand
 * colours.
 *
 * Read-only: the copy lives in ./brandKit/content.ts, the file list in
 * ./brandKit/files.ts (generated). Styles are scoped under .brand-kit.
 *
 * Rendered twice: inside the app at /brand-kit, and login-free at
 * /brand-kit/share via PublicBrandKit (publicView hides the share button).
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/ui/Icon';
import { useToast } from '../components/ui/Toast';
import { BRAND_KIT_CONTENT, Bio, LANGS, Lang } from './brandKit/content';
import LogoFiles from './brandKit/LogoFiles';

const LANG_STORAGE_KEY = 'mosy-brand-kit-lang';
const TAB_STORAGE_KEY = 'mosy-brand-kit-tab';

type Tab = 'profile' | 'logos';
type BioKey = Bio['key'];

const STYLES = `
.brand-kit { --bk-yellow: #FFBD09; --bk-green: #2B4138; }

/* segmented controls (tabs, language, bio length) */
.brand-kit .bk-seg { display: inline-flex; gap: 2px; padding: 3px; background: var(--bg-subtle); border: 1px solid var(--border); border-radius: 999px; }
.brand-kit .bk-seg button { border: 0; background: transparent; color: var(--ink-3); cursor: pointer; font: inherit; font-size: 12.5px; font-weight: 600; padding: 6px 14px; border-radius: 999px; display: inline-flex; align-items: center; gap: 6px; }
.brand-kit .bk-seg button[aria-selected="true"], .brand-kit .bk-seg button[aria-pressed="true"] { background: var(--bg-elevated); color: var(--ink); box-shadow: var(--shadow-sm); }
.brand-kit .bk-seg.dark button[aria-pressed="true"] { background: var(--bk-green); color: #fff; }
.brand-kit .bk-seg button:focus-visible { outline: 2px solid var(--bk-yellow); outline-offset: 2px; }
.brand-kit .bk-tabs { margin: 4px 0 20px; }

.brand-kit .bk-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.brand-kit .bk-row .section-title { margin: 0; }
.brand-kit .bk-hint { font-size: 12.5px; color: var(--ink-4); margin: 0; line-height: 1.5; }

/* intro */
.brand-kit .bk-intro { padding: 22px 24px; display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(220px, 1fr); gap: 28px; }
.brand-kit .bk-lede { font-size: 17px; font-weight: 600; margin: 0 0 8px; line-height: 1.35; }
.brand-kit .bk-sub { color: var(--ink-3); margin: 0; line-height: 1.55; font-size: 14px; }
.brand-kit .bk-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; align-content: start; }
.brand-kit .bk-stat { border-top: 3px solid var(--bk-yellow); padding-top: 6px; }
.brand-kit .bk-stat b { display: block; font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.1; }
.brand-kit .bk-stat span { font-size: 11.5px; color: var(--ink-4); }

/* bio */
.brand-kit .bk-bio { padding: 22px 26px 24px; }
.brand-kit .bk-txt { display: grid; gap: 12px; max-width: 76ch; font-size: 14.5px; line-height: 1.65; color: var(--ink-2); }
.brand-kit .bk-txt p { margin: 0; }
.brand-kit .bk-txt h4 { margin: 8px 0 0; font-size: 14px; font-weight: 700; color: var(--bk-green); }
.brand-kit .bk-txt h4 span { color: var(--ink-4); font-weight: 500; font-size: 12.5px; margin-left: 8px; }
.brand-kit .bk-bio-foot { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--border-soft); }
.brand-kit .bk-meta { font-size: 11.5px; color: var(--ink-4); letter-spacing: 0.04em; text-transform: uppercase; font-weight: 600; }

/* timeline: compact vertical */
.brand-kit .bk-tl { padding: 6px 24px 10px; }
.brand-kit .bk-ev { display: grid; grid-template-columns: 92px 1fr; gap: 16px; padding: 12px 0; border-bottom: 1px solid var(--border-soft); align-items: start; }
.brand-kit .bk-ev:last-child { border-bottom: 0; }
.brand-kit .bk-ev .year { font-weight: 700; font-size: 15px; font-variant-numeric: tabular-nums; display: flex; align-items: center; gap: 10px; white-space: nowrap; }
.brand-kit .bk-ev .year::before { content: ""; width: 10px; height: 10px; border-radius: 50%; background: var(--bk-yellow); flex: none; }
.brand-kit .bk-ev.now .year::before { background: var(--bk-green); }
.brand-kit .bk-ev .txt { margin: 0; font-size: 13.5px; line-height: 1.55; color: var(--ink-3); max-width: 80ch; }
.brand-kit .bk-ev .txt b { color: var(--ink); font-weight: 600; }

/* contact */
.brand-kit .bk-contact { background: var(--bk-green); color: #F1F0E8; border-radius: var(--r-lg); padding: 22px 26px; display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.brand-kit .bk-contact h3 { margin: 0 0 6px; color: var(--bk-yellow); font-size: 16px; font-weight: 700; }
.brand-kit .bk-contact p { margin: 0; color: #DDE5DF; line-height: 1.55; font-size: 13.5px; }
.brand-kit .bk-contact .lbl { font-size: 10.5px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; opacity: 0.7; margin-bottom: 4px; }
.brand-kit .bk-contact .col { display: grid; gap: 2px; align-content: start; font-size: 13.5px; }
.brand-kit .bk-contact a { color: inherit; text-decoration: none; font-weight: 600; }
.brand-kit .bk-contact a:hover { text-decoration: underline; }

/* logos tab */
.brand-kit .bk-colors { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
.brand-kit .bk-color { padding: 0; overflow: hidden; }
.brand-kit .bk-color .chip { height: 72px; border-bottom: 1px solid var(--border-soft); }
.brand-kit .bk-color .body { padding: 12px 14px 14px; display: grid; gap: 6px; }
.brand-kit .bk-color .name { font-weight: 700; font-size: 13.5px; display: flex; justify-content: space-between; gap: 8px; align-items: baseline; }
.brand-kit .bk-color .use { font-size: 12px; color: var(--ink-4); line-height: 1.45; }
.brand-kit .bk-color .codes { display: grid; gap: 2px; font-size: 12px; font-variant-numeric: tabular-nums; }
.brand-kit .bk-color .codes button { border: 0; background: transparent; padding: 3px 0; font: inherit; font-size: 12px; text-align: left; cursor: pointer; color: var(--ink-2); display: flex; gap: 10px; border-radius: 4px; }
.brand-kit .bk-color .codes button:hover { color: var(--ink); background: var(--bg-subtle); }
.brand-kit .bk-color .codes .k { color: var(--ink-4); width: 42px; flex: none; }

.brand-kit .bk-guide { padding: 16px 20px; display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.brand-kit .bk-guide ul { margin: 0; padding-left: 16px; font-size: 13px; line-height: 1.6; color: var(--ink-2); display: grid; gap: 3px; }
.brand-kit .bk-guide code { font-size: 11.5px; background: var(--bg-subtle); padding: 1px 6px; border-radius: 4px; }

.brand-kit .bk-logo-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.brand-kit .bk-logo-table th, .brand-kit .bk-logo-table td { padding: 10px 12px; border-top: 1px solid var(--border-soft); text-align: left; vertical-align: middle; }
.brand-kit .bk-logo-table thead th { border-top: 0; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-4); font-weight: 700; padding-top: 6px; }
.brand-kit .bk-logo-table .prev { box-sizing: border-box; width: 120px; height: 56px; border-radius: var(--r-sm); background: #FAFAF9; border: 1px solid var(--border-soft); padding: 8px; overflow: hidden; }
.brand-kit .bk-logo-table .prev.dark { background: var(--bk-green); }
.brand-kit .bk-logo-table .prev img { display: block; width: 100%; height: 100%; object-fit: contain; }
.brand-kit .bk-logo-table .cname { font-weight: 600; white-space: nowrap; }
.brand-kit .bk-files { display: flex; flex-wrap: wrap; gap: 6px; }
.brand-kit .bk-file { display: inline-flex; align-items: center; gap: 5px; text-decoration: none; padding: 4px 9px; font-size: 11.5px; }
.brand-kit .bk-file .ext { font-weight: 700; letter-spacing: 0.04em; }
.brand-kit .bk-file .size { color: var(--ink-4); font-weight: 400; }
.brand-kit .bk-table-wrap { overflow-x: auto; }

@media (max-width: 820px) {
  .brand-kit .bk-intro, .brand-kit .bk-contact, .brand-kit .bk-guide { grid-template-columns: 1fr; }
  .brand-kit .bk-ev { grid-template-columns: 1fr; gap: 4px; }
}
`;

const readStored = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
  try {
    const v = localStorage.getItem(key);
    if (v && (allowed as readonly string[]).includes(v)) return v as T;
  } catch {
    // storage unavailable — fall through to the default
  }
  return fallback;
};

const store = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // per-viewer convenience only
  }
};

/** Render a string that may contain <b>…</b> markers, without innerHTML. */
const renderEmphasis = (text: string) => {
  const parts = text.split(/(<b>.*?<\/b>)/g).filter(Boolean);
  return parts.map((part, i) => {
    const m = part.match(/^<b>(.*?)<\/b>$/);
    return m ? <b key={i}>{m[1]}</b> : <Fragment key={i}>{part}</Fragment>;
  });
};

const bioPlainText = (bio: Bio): string =>
  bio.paras
    .map((p) => (p.startsWith('H:') ? p.slice(2).replace('|', ' · ') : p))
    .join('\n\n');

const BioCard = ({ bio, copyLabel, copiedLabel }: { bio: Bio; copyLabel: string; copiedLabel: string }) => {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(bioPlainText(bio));
      setCopied(true);
    } catch {
      toast.push('Could not copy — select the text and copy it manually');
    }
  };

  return (
    <article className="card bk-bio">
      <div className="bk-txt">
        {bio.paras.map((p, i) => {
          if (p.startsWith('H:')) {
            const [h, u] = p.slice(2).split('|');
            return (
              <h4 key={i}>
                {h}
                {u && <span>{u}</span>}
              </h4>
            );
          }
          return <p key={i}>{p}</p>;
        })}
      </div>
      <div className="bk-bio-foot">
        <span className="bk-meta">{bio.meta}</span>
        <button type="button" className={`btn ${copied ? 'primary' : 'ghost'}`} onClick={handleCopy}>
          <Icon name={copied ? 'check' : 'copy'} size={13} /> {copied ? copiedLabel : copyLabel}
        </button>
      </div>
    </article>
  );
};

export const PUBLIC_BRAND_KIT_PATH = '/brand-kit/share';

const ShareButton = () => {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);
  const url = `${window.location.origin}${PUBLIC_BRAND_KIT_PATH}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      toast.push(`Public link: ${url}`);
    }
  };
  return (
    <button type="button" className={`btn ${copied ? 'primary' : 'ghost'}`} onClick={copy} title={url}>
      <Icon name={copied ? 'check' : 'link'} size={13} /> {copied ? 'Link copied' : 'Copy public link'}
    </button>
  );
};

const BrandKit = ({ publicView = false }: { publicView?: boolean }) => {
  const [tab, setTab] = useState<Tab>(() => readStored(TAB_STORAGE_KEY, ['profile', 'logos'] as const, 'profile'));
  const [lang, setLang] = useState<Lang>(() => readStored(LANG_STORAGE_KEY, ['nl', 'fr', 'en'] as const, 'nl'));
  const [bioKey, setBioKey] = useState<BioKey>('medium');
  const t = useMemo(() => BRAND_KIT_CONTENT[lang], [lang]);
  const bio = t.bios.find((b) => b.key === bioKey) ?? t.bios[0];

  useEffect(() => {
    document.title = 'Mosy · Brand kit';
  }, []);
  useEffect(() => store(LANG_STORAGE_KEY, lang), [lang]);
  useEffect(() => store(TAB_STORAGE_KEY, tab), [tab]);

  return (
    <div className="brand-kit">
      <style>{STYLES}</style>

      <div className="page-header">
        <div>
          <div className="page-eyebrow">{publicView ? 'LampTwist' : 'Marketing'}</div>
          <h1 className="page-title">Brand kit</h1>
          <p className="page-sub">
            {publicView
              ? 'LampTwist\u2019s company profile, logos and brand colours \u2014 free to copy and download for partners, press and agencies.'
              : 'LampTwist\u2019s company profile, logos and colours \u2014 ready to copy, download and share. The public link opens the same page without a mosy login.'}
          </p>
        </div>
        {!publicView && <ShareButton />}
      </div>

      <div className="bk-seg bk-tabs" role="tablist" aria-label="Brand kit sections">
        <button type="button" role="tab" aria-selected={tab === 'profile'} onClick={() => setTab('profile')}>
          <Icon name="user" size={13} /> Company profile
        </button>
        <button type="button" role="tab" aria-selected={tab === 'logos'} onClick={() => setTab('logos')}>
          <Icon name="image" size={13} /> Logos &amp; colours
        </button>
      </div>

      {tab === 'profile' && (
        <>
          <div className="bk-row">
            <p className="bk-hint">{t.sub}</p>
            <div className="bk-seg dark" role="group" aria-label="Language">
              {LANGS.map((l) => (
                <button key={l.code} type="button" onClick={() => setLang(l.code)} aria-pressed={lang === l.code}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div className="section">
            <div className="card bk-intro">
              <div>
                <p className="bk-lede">{t.lede}</p>
                <p className="bk-sub">{t.bios_p}</p>
              </div>
              <div className="bk-stats">
                {t.stats.map(([b, s]) => (
                  <div className="bk-stat" key={s}>
                    <b>{b}</b>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="section">
            <div className="bk-row">
              <div className="section-title">{t.bios_h}</div>
              <div className="bk-seg" role="tablist" aria-label="Bio length">
                {t.bios.map((b) => (
                  <button key={b.key} type="button" role="tab" aria-selected={b.key === bio.key} onClick={() => setBioKey(b.key)}>
                    {b.h}
                  </button>
                ))}
              </div>
            </div>
            <BioCard key={`${lang}-${bio.key}`} bio={bio} copyLabel={t.copy} copiedLabel={t.copied} />
          </div>

          <div className="section">
            <div className="bk-row">
              <div className="section-title">{t.tl_h}</div>
              <p className="bk-hint">{t.tl_p}</p>
            </div>
            <div className="card bk-tl">
              {t.tl.map(([year, txt], i, arr) => (
                <div className={`bk-ev ${i === arr.length - 1 ? 'now' : ''}`} key={year}>
                  <div className="year">{year}</div>
                  <p className="txt">{renderEmphasis(txt)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="section">
            <div className="bk-contact">
              <div className="col">
                <h3>{t.contact_h}</h3>
                <p>{t.contact_p}</p>
              </div>
              <div className="col">
                <div className="lbl">{t.contact_addr_lbl}</div>
                <div>LampTwist Showroom &amp; Experience Center</div>
                <div>Chaussée de Waterloo 1357A</div>
                <div>1180 Uccle · Brussels, Belgium</div>
                <div className="lbl" style={{ marginTop: 12 }}>{t.contact_web_lbl}</div>
                <a href="https://lamptwist.com" target="_blank" rel="noreferrer">lamptwist.com</a>
                <a href="https://b2b.lamptwist.com" target="_blank" rel="noreferrer">b2b.lamptwist.com</a>
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'logos' && <LogoFiles />}
    </div>
  );
};

export default BrandKit;
